
import { useEffect, useRef, useState } from 'react';
import NeuralNetworkBg from '../../../components/feature/NeuralNetworkBg';

const features = [
  {
    title: 'Dopamine Reset',
    description:
      'Restore natural reward sensitivity and motivation pathways that modern life has hijacked.',
    icon: 'ri-restart-line',
    gradient: 'from-brand-orange to-brand-pink',
    image:
      'https://public.readdy.ai/ai/img_res/7a516b09c46f2069a793e2d4ef8ece65.jpg',
  },
  {
    title: 'Nervous System Reconditioning',
    description: 'Shift from reactive fight-or-flight to a calm, regulated baseline state.',
    icon: 'ri-heart-pulse-line',
    gradient: 'from-brand-pink to-brand-magenta',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20flowing%20soft%20rose%20and%20mauve%20energy%20waves%20calming%20nervous%20system%20visualization%20smooth%20organic%20curves%20dark%20background%20gentle%20ethereal%20glow%20serene%20digital%20art%20minimal%20muted%20pastel%20tones&width=400&height=300&seq=feat-nervous-soft-1&orientation=landscape',
  },
  {
    title: 'Cognitive Control Training',
    description: 'Strengthen executive function, attention systems, and mental clarity.',
    icon: 'ri-focus-3-line',
    gradient: 'from-brand-magenta to-brand-violet',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20soft%20lavender%20and%20dusty%20violet%20geometric%20brain%20focus%20visualization%20delicate%20crystal%20structures%20gentle%20glowing%20edges%20dark%20background%20cognitive%20clarity%20digital%20art%20futuristic%20minimal%20muted%20tones&width=400&height=300&seq=feat-cognitive-soft-1&orientation=landscape',
  },
  {
    title: 'Emotional Processing',
    description: 'Build capacity for emotional regulation, resilience, and release emotions in a healthy, safe way with real time guided sessions.',
    icon: 'ri-emotion-line',
    gradient: 'from-brand-violet to-brand-teal',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20soft%20periwinkle%20and%20dusty%20teal%20water%20ripple%20emotional%20waves%20visualization%20gentle%20flowing%20light%20dark%20background%20peaceful%20introspective%20digital%20art%20ethereal%20minimal%20muted%20pastel%20tones&width=400&height=300&seq=feat-emotional-soft-1&orientation=landscape',
  },
  {
    title: 'Soma Shifts',
    description: 'Release stored tension and recalibrate deep body awareness and presence.',
    icon: 'ri-body-scan-line',
    gradient: 'from-brand-teal to-brand-cyan',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20soft%20sage%20teal%20and%20seafoam%20human%20body%20energy%20field%20visualization%20gentle%20glowing%20meridian%20lines%20somatic%20awareness%20dark%20background%20ethereal%20flowing%20light%20digital%20art%20minimal%20muted%20pastel&width=400&height=300&seq=feat-soma-soft-1&orientation=landscape',
  },
  {
    title: 'Brain Tools',
    description: 'Practical protocols for real-world cognitive demands and peak performance.',
    icon: 'ri-tools-line',
    gradient: 'from-brand-teal to-brand-cyan',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20soft%20aqua%20and%20pale%20cyan%20digital%20toolkit%20visualization%20gentle%20glowing%20geometric%20tools%20floating%20in%20dark%20space%20futuristic%20brain%20technology%20clean%20minimal%20digital%20art%20muted%20pastel%20tones&width=400&height=300&seq=feat-tools-soft-1&orientation=landscape',
  },
];

export default function WhatItDoes() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="features"
      ref={sectionRef}
      className="relative py-28 lg:py-36 px-6 lg:px-12 bg-white overflow-hidden"
    >
      {/* Neural pathways in background */}
      <NeuralNetworkBg
        color="154,123,212"
        nodeCount={20}
        opacity={0.06}
        speed={0.3}
        variant="light"
      />

      {/* Soft gradient accents */}
      <div
        className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-[0.04]"
        style={{
          background:
            'radial-gradient(circle, rgba(212,120,158,0.6) 0%, transparent 70%)',
        }}
      ></div>
      <div
        className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-[0.04]"
        style={{
          background:
            'radial-gradient(circle, rgba(77,184,199,0.6) 0%, transparent 70%)',
        }}
      ></div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-violet/5 border border-brand-violet/8 mb-8">
            <div className="w-2 h-2 rounded-full bg-brand-violet animate-pulse"></div>
            <span className="text-xs font-semibold text-brand-violet uppercase tracking-widest">
              Features
            </span>
          </div>

          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight leading-tight">
            A reconditioning system<br />
            for <span className="gradient-text">modern minds.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:shadow-2xl hover:shadow-brand-pink/10 hover:-translate-y-1 ${
                visible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Image */}
              <div className="relative w-full h-48 overflow-hidden">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent"></div>
                <div
                  className={`absolute top-4 left-4 w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${feature.gradient} shadow-lg`}
                >
                  <i className={`${feature.icon} text-lg text-white`}></i>
                </div>
              </div>

              {/* Content */}
              <div className="p-7 bg-gradient-to-br from-gray-50/80 to-white border border-gray-100/80 border-t-0 rounded-b-2xl">
                <div
                  className={`w-10 h-0.5 bg-gradient-to-r ${feature.gradient} rounded-full mb-4 group-hover:w-16 transition-all duration-500`}
                ></div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 tracking-tight">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
